import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../components/ui/Button';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';
import { api } from '../lib/api';
import { MARGE_TYPEN, MARGE_TYP_LABEL, projekteApi, type MargeTyp } from '../lib/projekte-api';
import { requireAccess } from '../lib/guards';

export const Route = createFileRoute('/projekte/neu')({
  beforeLoad: () => requireAccess('/api/v1/projekte'),
  component: NeuesProjektPage,
});

interface KundenListItem {
  id: string;
  kundennummer: string | null;
  firmenname: string;
}

export function NeuesProjektPage(): JSX.Element {
  const navigate = useNavigate();
  const [kundeId, setKundeId] = useState('');
  const [versicherungKundeId, setVersicherungKundeId] = useState('');
  const [bezeichnung, setBezeichnung] = useState('');
  const [schadennummer, setSchadennummer] = useState('');
  const [sachbearbeiter, setSachbearbeiter] = useState('');
  const [margeTyp, setMargeTyp] = useState<'' | MargeTyp>('');
  const [margeWert, setMargeWert] = useState('');

  const kundenQ = useQuery({
    queryKey: ['kunden', 'list'],
    queryFn: () => api<{ items: KundenListItem[] }>('/api/v1/kunden'),
  });

  const create = useMutation({
    mutationFn: () =>
      projekteApi.create({
        kundeId,
        bezeichnung: bezeichnung.trim(),
        ...(schadennummer.trim().length > 0 ? { schadennummer: schadennummer.trim() } : {}),
        ...(versicherungKundeId.length > 0 ? { versicherungKundeId } : {}),
        ...(sachbearbeiter.trim().length > 0 ? { sachbearbeiter: sachbearbeiter.trim() } : {}),
        ...(margeTyp !== '' ? { margeTyp } : {}),
        ...(margeTyp !== '' && margeWert.trim().length > 0 ? { margeWert: margeWert.trim() } : {}),
      }),
    onSuccess: ({ item }) => {
      void navigate({ to: '/projekte/$id', params: { id: item.id } });
    },
  });

  const canSubmit = kundeId.length > 0 && bezeichnung.trim().length > 0 && !create.isPending;

  return (
    <div className="max-w-2xl space-y-6">
      <PageHeader
        title="Neues Projekt"
        description="Schadens- oder Koordinations-Container für mehrere Aufträge desselben Kunden anlegen."
      />

      {create.error ? <ErrorBanner error={create.error as Error} /> : null}

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) create.mutate();
        }}
      >
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Kunde</span>
          <Select value={kundeId} onChange={(e) => setKundeId(e.target.value)} required>
            <option value="" disabled>
              – Kunde wählen –
            </option>
            {(kundenQ.data?.items ?? []).map((k) => (
              <option key={k.id} value={k.id}>
                {k.kundennummer ? `${k.kundennummer} · ` : ''}
                {k.firmenname}
              </option>
            ))}
          </Select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Bezeichnung</span>
          <Input
            value={bezeichnung}
            onChange={(e) => setBezeichnung(e.target.value)}
            maxLength={200}
            required
            placeholder="z. B. Wasserschaden Hauptstr. 12 — Sanierung"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            Schadennummer (optional)
          </span>
          <Input
            value={schadennummer}
            onChange={(e) => setSchadennummer(e.target.value)}
            placeholder="z. B. SCH-2026-00123"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            Versicherung (optional, eigener Kunden-Datensatz)
          </span>
          <Select
            value={versicherungKundeId}
            onChange={(e) => setVersicherungKundeId(e.target.value)}
          >
            <option value="">– Keine Versicherung –</option>
            {(kundenQ.data?.items ?? []).map((k) => (
              <option key={k.id} value={k.id}>
                {k.kundennummer ? `${k.kundennummer} · ` : ''}
                {k.firmenname}
              </option>
            ))}
          </Select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            Sachbearbeiter (optional)
          </span>
          <Input
            value={sachbearbeiter}
            onChange={(e) => setSachbearbeiter(e.target.value)}
            placeholder="Name des Sachbearbeiters bei der Versicherung"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            Marge-Typ (optional)
          </span>
          <Select value={margeTyp} onChange={(e) => setMargeTyp(e.target.value as '' | MargeTyp)}>
            <option value="">– Keine Marge konfigurieren –</option>
            {MARGE_TYPEN.map((t) => (
              <option key={t} value={t}>
                {MARGE_TYP_LABEL[t]}
              </option>
            ))}
          </Select>
        </label>

        {margeTyp ? (
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">
              Marge-Wert ({margeTyp === 'aufschlag' ? '%' : 'EUR'})
            </span>
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              value={margeWert}
              onChange={(e) => setMargeWert(e.target.value)}
              placeholder={margeTyp === 'aufschlag' ? 'z. B. 15' : 'z. B. 250.00'}
            />
          </label>
        ) : null}

        <div className="flex gap-3">
          <Button type="submit" disabled={!canSubmit}>
            {create.isPending ? 'Speichere…' : 'Anlegen'}
          </Button>
          <Button type="button" variant="ghost" onClick={() => void navigate({ to: '/projekte' })}>
            Abbrechen
          </Button>
        </div>
      </form>
    </div>
  );
}
